import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { chegouPorLinkDeSenha, registrarPermissaoDeSenha } from "./linkDeEmail";

// `carregando` existe para o guard de rota não decidir cedo demais: no primeiro
// render a sessão ainda não foi lida do storage, e tratar "ainda não sei" como
// "não logado" redirecionaria um aluno logado para /entrar a cada F5.
type EstadoAuth = { sessao: Session | null; carregando: boolean };

const Ctx = createContext<EstadoAuth>({ sessao: null, carregando: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoAuth>({ sessao: null, carregando: true });

  useEffect(() => {
    // A permissão de definir senha é anotada ANTES do `setEstado`, e a ordem é
    // requisito: RedefinirSenha lê `podeDefinirSenha` durante o render, então
    // anotar depois faria o primeiro render com sessão concluir "não autorizado"
    // e mostrar "link inválido" a quem clicou no link certo.
    //
    // O evento continua ignorado (`_evento`) de propósito: ele NÃO distingue
    // convite de login com senha — os dois emitem SIGNED_IN. O motivo está em
    // linkDeEmail.ts, com a linha do auth-js que prova.
    function anotar(sessao: Session | null) {
      if (sessao && chegouPorLinkDeSenha) registrarPermissaoDeSenha(sessao.user.id);
      setEstado({ sessao, carregando: false });
    }

    supabase.auth.getSession().then(({ data }) => anotar(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, sessao) => anotar(sessao));
    return () => sub.subscription.unsubscribe();
  }, []);

  return <Ctx.Provider value={estado}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
